import type { RealtimeCountryRow } from "../services/realtimeApi";
import type { CountryDailyRow } from "../components/realtime/CountryDailyHeatmap";
import type { CountrySplit } from "../components/realtime/CountryBreakdown";
import type { RoamingZone } from "../components/realtime/RoamingPulse";
import type { DateRange } from "../components/common/DateRangePicker";

// Pure aggregation over the country breakdown rows. Both realtime dashboards
// (single area and project) feed the same functions and differ only in which
// target ids they pass — so a change here lands on both surfaces at once.

/** Home market: everything else counts as roaming/international. */
export const DOMESTIC = "NO";

type MemberScope = Set<string> | null; // null = every target in the rows

const inScope = (scope: MemberScope, id: string) => !scope || scope.has(id);

/**
 * Per-country, per-day AVERAGE presence across the scoped areas.
 *
 * A mean rather than a running total: a sum over the range is in
 * subscriber-samples, so its size depends on polling frequency and on how the
 * backend bucketed the request — a number no reader can interpret. A mean is
 * just "people here", and it's stable at any granularity. Rows may arrive raw,
 * hourly or daily, so each (country, day, area) cell is a sample-weighted mean
 * over whatever rows cover it, and the areas are then summed — how many were
 * present across the scope at once, that day.
 */
export function dailyCountryRows(
  rows: RealtimeCountryRow[] | null,
  scope: MemberScope
): { days: string[]; rows: CountryDailyRow[] } {
  const empty = { days: [] as string[], rows: [] as CountryDailyRow[] };
  if (!rows) return empty;

  const acc = new Map<string, { num: number; den: number }>(); // country|day|area
  const dayset = new Set<string>();
  for (const r of rows) {
    const id = String(r.target_id);
    if (!inScope(scope, id)) continue;
    const day = String(r.timestamp).slice(0, 10);
    if (!day) continue;
    dayset.add(day);
    const key = `${r.country || "Unknown"}|${day}|${id}`;
    const cur = acc.get(key) ?? { num: 0, den: 0 };
    const w = r.sample_count ?? 1;
    cur.num += (r.subscriber_count || 0) * w;
    cur.den += w;
    acc.set(key, cur);
  }
  if (!acc.size) return empty;

  const perCountryDay = new Map<string, Map<string, number>>();
  acc.forEach((v, key) => {
    const [country, day] = key.split("|");
    const byDay = perCountryDay.get(country) ?? new Map<string, number>();
    byDay.set(day, (byDay.get(day) ?? 0) + (v.den ? v.num / v.den : 0));
    perCountryDay.set(country, byDay);
  });

  const days = [...dayset].sort();
  const built: CountryDailyRow[] = [...perCountryDay.entries()].map(([country, byDay]) => {
    const values = days.map((d) => (byDay.has(d) ? Math.round(byDay.get(d)!) : null));
    const present = values.filter((v): v is number => v != null);
    return {
      country,
      values,
      avg: present.length ? Math.round(present.reduce((s, v) => s + v, 0) / present.length) : 0,
      peak: present.length ? Math.max(...present) : 0,
    };
  });
  const ranked = built.sort((a, b) => b.avg - a.avg);
  return {
    days,
    // Home market first — it's the reference every other row is read against.
    rows: [...ranked.filter((r) => r.country === DOMESTIC), ...ranked.filter((r) => r.country !== DOMESTIC)],
  };
}

/** Ranked view of the same daily averages: magnitude + share + combined total. */
export function countrySplitFromDaily(rows: CountryDailyRow[]): CountrySplit | null {
  if (!rows.length) return null;
  const items = rows.map((r) => ({ country: r.country, count: r.avg }));
  return {
    items,
    total: items.reduce((s, i) => s + i.count, 0),
    latestTs: 0, // unused: the panel subtitle states the range instead
  };
}

/**
 * Roaming pulse: international (non-domestic) presence per zone at the newest
 * sample, and the change against the sample NEAREST `intervalMin` earlier.
 * Nearest, not "N samples back": the cadence is ~9.6 min, not exactly 10, so
 * counting would drift and silently mislabel the interval.
 */
export function roamingZonesFrom(
  rows: RealtimeCountryRow[] | null,
  scope: MemberScope,
  intervalMin: number
): { zones: RoamingZone[]; updatedAt: number | null } {
  if (!rows) return { zones: [], updatedAt: null };

  const per = new Map<string, Map<string, { intl: number; total: number; by: Map<string, number> }>>();
  const names = new Map<string, string>();
  let newest = 0;
  for (const r of rows) {
    const id = String(r.target_id);
    if (!inScope(scope, id)) continue;
    names.set(id, r.target_name || id);
    const stamp = String(r.timestamp);
    newest = Math.max(newest, +new Date(stamp));
    const byStamp = per.get(id) ?? new Map();
    const cell = byStamp.get(stamp) ?? { intl: 0, total: 0, by: new Map<string, number>() };
    const n = r.subscriber_count || 0;
    cell.total += n;
    if (r.country !== DOMESTIC) {
      cell.intl += n;
      cell.by.set(r.country, (cell.by.get(r.country) || 0) + n);
    }
    byStamp.set(stamp, cell);
    per.set(id, byStamp);
  }

  const zones: RoamingZone[] = [];
  per.forEach((byStamp, id) => {
    const stamps = [...byStamp.keys()].sort();
    if (!stamps.length) return;
    const latestStamp = stamps[stamps.length - 1];
    const last = byStamp.get(latestStamp)!;
    const cutoff = +new Date(latestStamp) - intervalMin * 60 * 1000;
    let refStamp: string | null = null;
    let bestGap = Infinity;
    for (const st of stamps.slice(0, -1)) {
      const gap = Math.abs(+new Date(st) - cutoff);
      if (gap < bestGap) {
        bestGap = gap;
        refStamp = st;
      }
    }
    const prev = refStamp ? byStamp.get(refStamp)! : null;
    zones.push({
      id,
      name: names.get(id) || id,
      intlNow: last.intl,
      totalNow: last.total,
      delta: prev ? last.intl - prev.intl : 0,
      series: stamps.map((t) => byStamp.get(t)!.intl),
      topCountries: [...last.by.entries()]
        .map(([country, count]) => ({ country, count }))
        .sort((a, b) => b.count - a.count),
      sharePct: last.total ? Math.round((last.intl / last.total) * 100) : 0,
    });
  });
  return { zones: zones.sort((a, b) => b.intlNow - a.intlNow), updatedAt: newest || null };
}

/** Plain-language description of a date window, for panel subtitles. */
export function rangeLabelFor(range: DateRange): string {
  if (!range.from) return "all time";
  const to = range.to ? new Date(range.to).toLocaleDateString() : "now";
  return `${new Date(range.from).toLocaleDateString()} – ${to}`;
}
